import { getMinPrice, getTotalPrice } from '@shared/lib/money';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { InsuranceProgramFragment, SelectedInsuranceFragment } from '@websky/graphql';
import { useInsurancePageWrapper } from '../../../InsurancePage/hooks';
import { getInsurances, getIsConfirmed, getOrder } from '../../../Checkout/store/order/selectors';
import { getInsurancePriceToPay } from '../../../Checkout/store/orderPrice/selectors';
import { useToggleable, useIataCode } from '../../../hooks';
import { hasIntersection } from '../../../utils';
import { analytics_removeInsurance } from '../../../Insurance/utils';
import { getSelectedInsuranceDownloadAction } from '@shared/lib/insurance';
import {
	addLoadingServiceParam,
	clearLoadingServicesParams
} from '../../../Checkout/store/loadingServicesParams/actions';
import { LoadingServiceType } from '../../../Checkout/store/loadingServicesParams/reducers';
import { getIsLoadingServiceByParams } from '../../../Checkout/store/loadingServicesParams/selectors';
import { useQueryEventToggleable } from '../../../Checkout/components/Checkout/hooks';
import { SpecialServiceType } from '../../../AIPurchasePredictionService/types';
import { isAlwaysOpenInsurancePopup } from './utils';

export const useInsuranceUpsale = (insurancePrograms?: InsuranceProgramFragment[]) => {
	const insurancePageWrapper = useInsurancePageWrapper({ strategy: 'checkout-order-service', insurancePrograms });

	const dispatch = useDispatch();
	const iataCode = useIataCode();

	const isConfirmed = useSelector(getIsConfirmed);
	const insurances = useSelector(getInsurances);
	const insuranceTotalPrice = useSelector(getInsurancePriceToPay);
	const order = useSelector(getOrder);

	const isLoadingByParams = useSelector(
		getIsLoadingServiceByParams({ type: LoadingServiceType.Insurance }, ['type'])
	);

	const { isOpen, open, close } = useToggleable(false);

	const availablePrograms = useMemo(() => {
		if (!insurancePrograms?.length) {
			return insurances.insurancePrograms;
		}

		return insurances.insurancePrograms.filter(program =>
			insurancePrograms.some(available => available.code === program.code)
		);
	}, [insurances.insurancePrograms, insurancePrograms]);

	useQueryEventToggleable({
		serviceTypes: [SpecialServiceType.Insurance],
		insuranceCodes: availablePrograms.map(program => program.code),
		onOpen: open
	});

	const selectedPrograms = useMemo(() => {
		const selectedProgramsMap = new Map<string, SelectedInsuranceFragment>();

		if (insurances.selectedInsurances?.length) {
			insurances.selectedInsurances.forEach(insurance => {
				const insuranceCode = insurance.insuranceProgram.code;
				const isAvailableInsurance = availablePrograms.some(program => program.code === insuranceCode);

				// exclude duplicate selected insurances
				if (isAvailableInsurance && !selectedProgramsMap.has(insuranceCode)) {
					selectedProgramsMap.set(insuranceCode, insurance);
				}
			});
		}

		return [...selectedProgramsMap.values()];
	}, [insurances.selectedInsurances, availablePrograms]);

	const { confirmedInsurances, unpaidInsurances } = useMemo(() => {
		const confirmed: SelectedInsuranceFragment[] = [];
		const unpaid: SelectedInsuranceFragment[] = [];

		for (const insurance of selectedPrograms) {
			if (insurance.policyUrl || insurance.policyNumber) {
				confirmed.push(insurance);
			} else {
				unpaid.push(insurance);
			}
		}

		return { confirmedInsurances: confirmed, unpaidInsurances: unpaid };
	}, [selectedPrograms]);

	const insuranceSummaryServices = useMemo(() => {
		const additionalServices = insurancePageWrapper.order.additionalServices;

		return {
			...additionalServices,
			insurances: {
				...additionalServices.insurances,
				insurancePrograms: availablePrograms
			}
		};
	}, [insurancePageWrapper.order.additionalServices, availablePrograms]);

	const totalPrice = useMemo(() => {
		if (!insurancePrograms?.length) {
			return insuranceTotalPrice;
		}

		return getTotalPrice(unpaidInsurances, insurance => insurance.insuranceProgram.totalPrice);
	}, [insurancePrograms, unpaidInsurances, insuranceTotalPrice]);

	const minPrice = useMemo(() => {
		return getMinPrice(availablePrograms, insurance => insurance.totalPrice);
	}, [availablePrograms]);

	const isDifferentPrices = useMemo(
		() => new Set(availablePrograms.map(insurance => insurance.totalPrice.amount)).size > 1,
		[availablePrograms]
	);

	const singleInsurance = useMemo(() => {
		if (availablePrograms.length === 1) {
			return availablePrograms[0];
		}

		return null;
	}, [availablePrograms]);

	const canModify = useMemo(() => {
		return singleInsurance ? !confirmedInsurances.length : confirmedInsurances.length !== availablePrograms.length;
	}, [singleInsurance, confirmedInsurances, availablePrograms]);

	const isSelected = useMemo(
		() =>
			hasIntersection(
				selectedPrograms.map(({ insuranceProgram: { code } }) => code),
				insurancePrograms?.map(program => program.code) ?? []
			),
		[selectedPrograms, insurancePrograms]
	);

	const isReadonly = useMemo(() => {
		return isSelected && (!availablePrograms.some(program => program.canBeAdded) || !canModify);
	}, [isSelected, availablePrograms, canModify]);

	const isSingleInsuranceMode = useMemo(() => !!singleInsurance && !isAlwaysOpenInsurancePopup(iataCode), [
		singleInsurance,
		iataCode
	]);

	const isSingleToggleMode = useMemo(() => isSingleInsuranceMode && !isReadonly, [isSingleInsuranceMode, isReadonly]);

	const singleInsuranceDownload = useMemo(() => {
		if (!singleInsurance || !isSelected || !selectedPrograms[0]) {
			return null;
		}

		return getSelectedInsuranceDownloadAction(
			selectedPrograms[0],
			isConfirmed,
			insurancePageWrapper.onDownloadNasInsurance
		);
	}, [singleInsurance, isSelected, selectedPrograms, isConfirmed, insurancePageWrapper.onDownloadNasInsurance]);

	const onStartLoading = () => {
		dispatch(addLoadingServiceParam({ type: [LoadingServiceType.Insurance] }));
	};

	const onStopLoading = () => {
		dispatch(clearLoadingServicesParams());
	};

	const onAddInsurance = useCallback(
		async (code: string) => {
			onStartLoading();
			const selectedProgramsSet = new Set<string>();

			insurances.selectedInsurances.forEach(insurance =>
				selectedProgramsSet.add(insurance.insuranceProgram.code)
			);

			selectedProgramsSet.add(code);

			await insurancePageWrapper.saveOrderServicesWithParams([...selectedProgramsSet]);
			onStopLoading();
		},
		[insurances.selectedInsurances, insurancePageWrapper.saveOrderServicesWithParams, dispatch]
	);

	const onDeleteInsurance = useCallback(
		async (code: string) => {
			onStartLoading();
			const selectedProgramsSet = new Set<string>();

			insurances.selectedInsurances?.forEach(insurance =>
				selectedProgramsSet.add(insurance.insuranceProgram.code)
			);

			selectedProgramsSet.delete(code);

			const deletingProgram = insurances.selectedInsurances.find(
				selected => selected.insuranceProgram.code === code
			)?.insuranceProgram;

			if (deletingProgram) {
				analytics_removeInsurance(deletingProgram, order.travellers);
			}

			await insurancePageWrapper.saveOrderServicesWithParams([...selectedProgramsSet]);
			onStopLoading();
		},
		[insurances.selectedInsurances, insurancePageWrapper.saveOrderServicesWithParams, dispatch]
	);

	const onAddSingle = useCallback(async () => {
		if (!singleInsurance) {
			return;
		}

		await onAddInsurance(singleInsurance.code);
	}, [singleInsurance, onAddInsurance]);

	const onDeleteSingle = useCallback(async () => {
		const codeToDelete = singleInsurance?.code ?? selectedPrograms[0]?.insuranceProgram?.code;

		if (!codeToDelete) {
			return;
		}

		await onDeleteInsurance(codeToDelete);
	}, [singleInsurance, selectedPrograms, onDeleteInsurance]);

	const onConfirm = useCallback(() => insurancePageWrapper.saveOrderServicesCallback(close), [
		insurancePageWrapper.saveOrderServicesCallback
	]);

	const onClose = useCallback(() => {
		insurancePageWrapper.onHideImportantInsurancesError();
		insurancePageWrapper.onContinue(close);
	}, [insurancePageWrapper.onHideImportantInsurancesError, insurancePageWrapper.onContinue]);

	const handleAgree = useCallback(async () => {
		if (insurancePageWrapper.priorityInsurance) {
			await insurancePageWrapper.handleAgree();
			insurancePageWrapper.closeUpsaleModal();
			close();

			return;
		}

		await insurancePageWrapper.handleAgree();
	}, [insurancePageWrapper.priorityInsurance, insurancePageWrapper.handleAgree, close]);

	const handleDisagree = useCallback(async () => {
		await insurancePageWrapper.handleDisagree();
		close();
	}, [insurancePageWrapper.handleDisagree, close]);

	return {
		// methods
		open,
		handleAgree,
		handleDisagree,
		onConfirm,
		onClose,
		onAddInsurance,
		onDeleteInsurance,
		onAddSingle,
		onDeleteSingle,
		onDownloadVskInsurance: insurancePageWrapper.onDownloadNasInsurance,
		setInsurancePriceToPay: insurancePageWrapper.setInsurancePriceToPay,
		setSaveOrderServicesParams: insurancePageWrapper.setSaveOrderServicesParams,
		setIsNeedToSendSaveReq: insurancePageWrapper.setIsNeedToSendSaveReq,

		// data
		order: insurancePageWrapper.order,
		insurancePriceToPay: insurancePageWrapper.insurancePriceToPay,
		priorityInsurance: insurancePageWrapper.priorityInsurance,
		availablePrograms,
		selectedPrograms,
		insuranceSummaryServices,
		minPrice,
		totalPrice,
		singleInsurance,
		singleInsuranceDownload,
		isOpen,
		isConfirmed,
		isSelected,
		isReadonly,
		isSingleInsuranceMode,
		isSingleToggleMode,
		isDifferentPrices,
		isUpsaleOpen: insurancePageWrapper.isUpsaleOpen,
		isLoading: insurancePageWrapper.isLoading || isLoadingByParams,
		isShowImportantInsurancesError: insurancePageWrapper.isShowImportantInsurancesError,
		canModify
	};
};

export const useInsuranceGroupTexts = (groupName?: string, insurancePrograms?: InsuranceProgramFragment[]) => {
	const { t, i18n } = useTranslation('Checkout');

	const titleKey = `Checkout:InsuranceGroupTitle_${groupName}`;
	const descrKey = `Checkout:InsuranceGroupDescr_${groupName}`;

	const title = i18n.exists(titleKey) ? t(titleKey) : t('InsuranceGroupTitle_default');
	const descr = i18n.exists(descrKey) ? t(descrKey) : t('InsuranceGroupDescr_default');

	if (insurancePrograms?.length === 1) {
		const singleInsurance = insurancePrograms[0];

		return {
			title: singleInsurance.name ?? title,
			descr: singleInsurance.description ?? descr,
			options: singleInsurance.options?.length ? singleInsurance.options.map(({ name }) => name) : []
		};
	}

	return { title, descr, options: [] };
};
